import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Stack,
  Tabs,
  Tab,
  Chip,
  Divider,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardContent,
  alpha,
  LinearProgress,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Pagination,
  useTheme,
  MenuItem,
  MenuItem as MuiMenuItem
} from "@mui/material";
import {
  Group as GroupIcon,
  People as PeopleIcon,
  Percent as PercentIcon,
  Visibility as VisibilityIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Edit as EditIcon
} from "@mui/icons-material";
import axios from "axios";

const BASE_URL = process.env.REACT_APP_BASE_URL;

export default function GroupClassesPage() {
  const theme = useTheme();
  const [classes, setClasses] = useState([]);
  const [allEnrollments, setAllEnrollments] = useState([]);
  const [commission, setCommission] = useState(10);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("classes");
  const [page, setPage] = useState(1);
  const [rowsPerPage] = useState(10);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success"
  });

  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsClassInfo, setDetailsClassInfo] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [editSaving, setEditSaving] = useState(false);

  const [studentsDialogOpen, setStudentsDialogOpen] = useState(false);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedClassInfo, setSelectedClassInfo] = useState(null);
  const [classStudents, setClassStudents] = useState([]);
  const [brandSelectionMap, setBrandSelectionMap] = useState({});

  const fetchClasses = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/admin/group-classes`);
      const rows = res.data.data || [];
      setClasses(rows);
      setBrandSelectionMap((prev) => {
        const next = { ...prev };
        rows.forEach((row) => {
          if (!next[row.id]) {
            next[row.id] = row.brand_classification || "normal_tutor";
          }
        });
        return next;
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to load group classes",
        severity: "error"
      });
    }
  };

  const fetchAllEnrollments = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/api/admin/group-classes/enrollments`);
      setAllEnrollments(res.data.data || []);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to load enrolled students",
        severity: "error"
      });
    }
  };

  const fetchCommission = async () => {
    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/group-classes/commission`
      );
      setCommission(res.data.commission);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to load commission",
        severity: "error"
      });
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await Promise.all([fetchClasses(), fetchAllEnrollments(), fetchCommission()]);
      setLoading(false);
    };
    fetchData();
  }, []);

  const handleStatusChange = async (id, status) => {
    const brandClassification = brandSelectionMap[id];
    if (status === "approved" && !brandClassification) {
      setSnackbar({
        open: true,
        message: "Select class brand before approving",
        severity: "error"
      });
      return;
    }

    try {
      await axios.put(
        `${BASE_URL}/api/admin/group-classes/${id}/status`,
        {
          status,
          brand_classification: status === "approved" ? brandClassification : null,
        }
      );
      await Promise.all([fetchClasses(), fetchAllEnrollments()]);
      setSnackbar({
        open: true,
        message: `Class ${status} successfully`,
        severity: "success"
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update class status",
        severity: "error"
      });
    }
  };

  const handleUpdateCommission = async () => {
    try {
      setLoading(true);
      await axios.put(
        `${BASE_URL}/api/admin/group-classes/commission`,
        { commission }
      );
      setSnackbar({
        open: true,
        message: "Commission updated successfully",
        severity: "success"
      });
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update commission",
        severity: "error"
      });
    } finally {
      setLoading(false);
    }
  };

  const openDetailsDialog = (row) => {
    setDetailsClassInfo(row);
    setEditFormData({ ...row });
    setEditMode(false);
    setDetailsDialogOpen(true);
  };

  const closeDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setEditMode(false);
    setEditFormData({});
  };

  const handleEditClick = () => {
    setEditMode(true);
    setEditFormData({ ...detailsClassInfo });
  };

  const handleEditChange = (field, value) => {
    setEditFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSaveEdit = async () => {
    try {
      setEditSaving(true);
      const classUpdatePayload = {
        title: editFormData.title,
        description: editFormData.description,
        price: Number(editFormData.price),
        maxStudents: Number(editFormData.max_students),
        endDate: editFormData.end_date,
        curriculum: editFormData.curriculum,
        videoLink: editFormData.video_link,
        classLink: editFormData.class_link,
        certificateType: editFormData.certificate_type || "none",
      };

      const statusToSave = editFormData.status || "pending";

      await axios.put(
        `${BASE_URL}/api/tutors/update-group-class/${editFormData.id}`,
        classUpdatePayload
      );

      if (["approved", "rejected"].includes(statusToSave)) {
        await axios.put(
          `${BASE_URL}/api/admin/group-classes/${editFormData.id}/status`,
          {
            status: statusToSave,
            brand_classification: editFormData.brand_classification,
          }
        );
      }

      setSnackbar({
        open: true,
        message: "Group class updated successfully",
        severity: "success",
      });

      setEditMode(false);
      setDetailsClassInfo(editFormData);
      await fetchClasses();
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to update group class",
        severity: "error",
      });
    } finally {
      setEditSaving(false);
    }
  };

  const handleOpenStudents = async (row) => {
    setStudentsDialogOpen(true);
    setStudentsLoading(true);
    setSelectedClassInfo(null);
    setClassStudents([]);

    try {
      const res = await axios.get(
        `${BASE_URL}/api/admin/group-classes/${row.id}/enrollments`
      );
      setSelectedClassInfo(res.data?.data?.class || row);
      setClassStudents(res.data?.data?.enrollments || []);
    } catch (err) {
      console.error(err);
      setSnackbar({
        open: true,
        message: "Failed to load class students",
        severity: "error"
      });
    } finally {
      setStudentsLoading(false);
    }
  };

  const closeStudentsDialog = () => {
    setStudentsDialogOpen(false);
    setSelectedClassInfo(null);
    setClassStudents([]);
  };

  // Calculate enrollment counts safely
  const enrollmentCountMap = allEnrollments.reduce((acc, item) => {
    if (item && item.class_id) {
      const current = acc[item.class_id] || 0;
      acc[item.class_id] = current + 1;
    }
    return acc;
  }, {});

  // Calculate summary stats
  const totalClasses = classes.length;
  const totalEnrollments = allEnrollments.length;
  const pendingClasses = classes.filter(c => c.status === 'pending').length;
  const totalRevenue = allEnrollments.reduce((sum, e) => sum + (Number(e.amount_paid) || 0), 0);
  const platformEarnings = totalRevenue * (commission / 100);

  console.log(pendingClasses)
  // Get current page data
  const getCurrentPageData = () => {
    const source = activeTab === "classes" ? classes : allEnrollments;
    const start = (page - 1) * rowsPerPage;
    const end = start + rowsPerPage;
    return source.slice(start, end);
  };

  const paginatedData = getCurrentPageData();
  const totalPages = Math.ceil(
    (activeTab === "classes" ? classes.length : allEnrollments.length) / rowsPerPage
  );

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            color: 'transparent',
            mb: 1
          }}
        >
          Group Classes Management
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Manage group classes, track enrollments, and configure platform commission
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', lg: 'repeat(4, 1fr)' },
        gap: 3,
        mb: 4
      }}>
        <Card sx={{ 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }} gutterBottom>
                  Total Classes
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {totalClasses}
                </Typography>
              </Box>
              <GroupIcon sx={{ fontSize: 32, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: 'white',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }} gutterBottom>
                  Total Enrollments
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {totalEnrollments}
                </Typography>
              </Box>
              <PeopleIcon sx={{ fontSize: 32, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
          color: 'white',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }} gutterBottom>
                  Platform Commission
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  {commission}%
                </Typography>
              </Box>
              <PercentIcon sx={{ fontSize: 32, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>

        <Card sx={{ 
          borderRadius: 3,
          background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
          color: 'white',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'translateY(-4px)' }
        }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Box>
                <Typography variant="subtitle2" sx={{ opacity: 0.9 }} gutterBottom>
                  Platform Earnings
                </Typography>
                <Typography variant="h4" fontWeight="bold">
                  ₦{platformEarnings.toLocaleString()}
                </Typography>
              </Box>
              <TrendingUpIcon sx={{ fontSize: 32, opacity: 0.8 }} />
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Commission Settings Card */}
      <Card sx={{ mb: 4, borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold' }}>
            Platform Commission Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            This percentage is removed from each paid group class booking. The remainder is credited to tutor balance.
          </Typography>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="flex-start">
            <TextField
              type="number"
              label="Commission Percentage (%)"
              value={commission}
              inputProps={{ min: 0, max: 100, step: 0.5 }}
              onChange={(e) => setCommission(e.target.value)}
              sx={{ width: { xs: '100%', sm: 200 } }}
            />
            <Button
              variant="contained"
              onClick={handleUpdateCommission}
              disabled={loading}
              sx={{ 
                px: 4,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #5a67d8 0%, #6b46a0 100%)'
                }
              }}
            >
              Save Changes
            </Button>
          </Stack>
        </CardContent>
      </Card>

      {/* Tabs Section */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          mb: 3,
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        <Box sx={{ borderBottom: 1, borderColor: 'divider', px: 2, pt: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(event, value) => {
              setActiveTab(value);
              setPage(1);
            }}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '0.9rem',
                minHeight: 48,
                '&.Mui-selected': {
                  color: 'primary.main'
                }
              }
            }}
          >
            <Tab 
              value="classes" 
              icon={<GroupIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start"
              label={`Group Classes (${classes.length})`} 
            />
            <Tab 
              value="enrollments" 
              icon={<PeopleIcon sx={{ fontSize: 20 }} />} 
              iconPosition="start"
              label={`All Enrolled Students (${allEnrollments.length})`} 
            />
          </Tabs>
        </Box>
      </Paper>

      {/* Data Table */}
      <Paper 
        elevation={0} 
        sx={{ 
          borderRadius: 3,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
        }}
      >
        {loading ? (
          <Box sx={{ p: 4 }}>
            <LinearProgress />
          </Box>
        ) : (
          <>
            <TableContainer sx={{ maxHeight: 'calc(100vh - 500px)', overflowX: 'auto' }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    {activeTab === "classes" ? (
                      <>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Title</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Tutor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Price</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Capacity</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Start Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>End Date</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Brand Class</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Actions</TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Enrollment ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Class</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Tutor</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Student</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }} align="right">Amount</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Payment</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: '#fafafa' }}>Booked At</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={activeTab === "classes" ? 10 : 8} align="center" sx={{ py: 8 }}>
                        <Typography variant="body1" color="text.secondary">
                          No {activeTab === "classes" ? "group classes" : "enrollments"} found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow key={row.id} hover>
                        {activeTab === "classes" ? (
                          <>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>
                              <Typography fontWeight={500}>{row.title || '-'}</Typography>
                            </TableCell>
                            <TableCell>{row.tutor_name || '-'}</TableCell>
                            <TableCell align="right">
                              <Typography fontWeight={600}>
                                ₦{Number(row.price || 0).toLocaleString()}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip 
                                size="small"
                                label={`${enrollmentCountMap[row.id] || 0}/${row.max_students || 0}`}
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {row.start_date ? new Date(row.start_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              {row.end_date ? new Date(row.end_date).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={String(row.status || 'pending').toUpperCase()}
                                color={
                                  row.status === 'approved' ? 'success' : 
                                  row.status === 'rejected' ? 'error' : 
                                  'warning'
                                }
                                variant="filled"
                              />
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={
                                  row.brand_classification === 'big_brand_partner'
                                    ? 'Big Brand / Partner'
                                    : 'Normal Tutor'
                                }
                                sx={{
                                  bgcolor: row.brand_classification === 'big_brand_partner' ? '#fbbf24' : '#cbd5e1',
                                  color: '#0f172a',
                                  fontWeight: 700,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              <Stack direction="row" spacing={1}>
                                <IconButton
                                  size="small"
                                  onClick={() => openDetailsDialog(row)}
                                  title="View Details"
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                  size="small"
                                  onClick={() => handleOpenStudents(row)}
                                  title="View Students"
                                >
                                  <PeopleIcon fontSize="small" />
                                </IconButton>
                                {row.status === 'pending' && (
                                  <>
                                    <TextField
                                      select
                                      size="small"
                                      value={brandSelectionMap[row.id] || "normal_tutor"}
                                      onChange={(e) => setBrandSelectionMap((prev) => ({ ...prev, [row.id]: e.target.value }))}
                                      sx={{ minWidth: 170 }}
                                    >
                                      <MenuItem value="big_brand_partner">Big Brand / Partner</MenuItem>
                                      <MenuItem value="normal_tutor">Normal Tutor</MenuItem>
                                    </TextField>
                                    <IconButton
                                      size="small"
                                      color="success"
                                      onClick={() => handleStatusChange(row.id, "approved")}
                                      title="Approve"
                                    >
                                      <CheckCircleIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton
                                      size="small"
                                      color="error"
                                      onClick={() => handleStatusChange(row.id, "rejected")}
                                      title="Reject"
                                    >
                                      <CancelIcon fontSize="small" />
                                    </IconButton>
                                  </>
                                )}
                              </Stack>
                            </TableCell>
                          </>
                        ) : (
                          <>
                            <TableCell>{row.id}</TableCell>
                            <TableCell>{row.class_title || '-'}</TableCell>
                            <TableCell>{row.tutor_name || '-'}</TableCell>
                            <TableCell>{row.student_name || '-'}</TableCell>
                            <TableCell>{row.student_email || '-'}</TableCell>
                            <TableCell align="right">
                              ₦{Number(row.amount_paid || 0).toLocaleString()}
                            </TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={String(row.payment_status || '-').toUpperCase()}
                                color={row.payment_status === 'paid' ? 'success' : 'default'}
                                variant={row.payment_status === 'paid' ? 'filled' : 'outlined'}
                              />
                            </TableCell>
                            <TableCell>
                              {row.created_at ? new Date(row.created_at).toLocaleString() : '-'}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3, borderTop: 1, borderColor: 'divider' }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, newPage) => setPage(newPage)}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Class Details Dialog */}
      <Dialog open={detailsDialogOpen} onClose={closeDetailsDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{editMode ? 'Edit' : 'Group Class'} Details</Typography>
            <IconButton onClick={closeDetailsDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {!detailsClassInfo ? (
            <Typography>No class selected</Typography>
          ) : (
            <Stack spacing={2}>
              {editMode ? (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Title"
                      value={editFormData.title || ""}
                      onChange={(e) => handleEditChange("title", e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Tutor"
                      value={editFormData.tutor_name || ""}
                      disabled
                      fullWidth
                      size="small"
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Price (₦)"
                      type="number"
                      value={editFormData.price || ""}
                      onChange={(e) => handleEditChange("price", e.target.value)}
                      fullWidth
                      size="small"
                    />
                    <TextField
                      label="Max Students"
                      type="number"
                      value={editFormData.max_students || ""}
                      onChange={(e) => handleEditChange("max_students", e.target.value)}
                      fullWidth
                      size="small"
                    />
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      label="Start Date"
                      type="datetime-local"
                      value={editFormData.start_date ? new Date(editFormData.start_date).toISOString().slice(0, 16) : ""}
                      onChange={(e) => handleEditChange("start_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                      label="End Date"
                      type="datetime-local"
                      value={editFormData.end_date ? new Date(editFormData.end_date).toISOString().slice(0, 16) : ""}
                      onChange={(e) => handleEditChange("end_date", e.target.value)}
                      fullWidth
                      size="small"
                      InputLabelProps={{ shrink: true }}
                    />
                  </Stack>

                  <TextField
                    label="Description"
                    value={editFormData.description || ""}
                    onChange={(e) => handleEditChange("description", e.target.value)}
                    multiline
                    rows={3}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Curriculum"
                    value={editFormData.curriculum || ""}
                    onChange={(e) => handleEditChange("curriculum", e.target.value)}
                    multiline
                    rows={2}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Video Link"
                    value={editFormData.video_link || ""}
                    onChange={(e) => handleEditChange("video_link", e.target.value)}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Class Link"
                    value={editFormData.class_link || ""}
                    onChange={(e) => handleEditChange("class_link", e.target.value)}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    label="Coupon Code"
                    value={editFormData.coupon_code || ""}
                    onChange={(e) => handleEditChange("coupon_code", e.target.value)}
                    fullWidth
                    size="small"
                  />

                  <TextField
                    select
                    label="Brand Classification"
                    value={editFormData.brand_classification || "normal_tutor"}
                    onChange={(e) => handleEditChange("brand_classification", e.target.value)}
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="normal_tutor">Normal Tutor</MenuItem>
                    <MenuItem value="big_brand_partner">Big Brand / Partner</MenuItem>
                  </TextField>
                </>
              ) : (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Title</Typography>
                      <Typography fontWeight={700}>{detailsClassInfo.title || "-"}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Tutor</Typography>
                      <Typography fontWeight={700}>{detailsClassInfo.tutor_name || "-"}</Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Price</Typography>
                      <Typography fontWeight={700}>₦{Number(detailsClassInfo.price || 0).toLocaleString()}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Seats</Typography>
                      <Typography fontWeight={700}>{detailsClassInfo.max_students || 0}</Typography>
                    </Box>
                  </Stack>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">Start Date</Typography>
                      <Typography fontWeight={700}>
                        {detailsClassInfo.start_date ? new Date(detailsClassInfo.start_date).toLocaleString() : "-"}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" color="text.secondary">End Date</Typography>
                      <Typography fontWeight={700}>
                        {detailsClassInfo.end_date ? new Date(detailsClassInfo.end_date).toLocaleString() : "-"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider />

                  <Box>
                    <Typography variant="caption" color="text.secondary">Description</Typography>
                    <Typography>{detailsClassInfo.description || "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Curriculum</Typography>
                    <Typography>{detailsClassInfo.curriculum || "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Video Link</Typography>
                    <Typography sx={{ wordBreak: "break-all" }}>{detailsClassInfo.video_link || "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Class Link</Typography>
                    <Typography sx={{ wordBreak: "break-all" }}>{detailsClassInfo.class_link || "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Coupon Code</Typography>
                    <Typography fontWeight={700}>{detailsClassInfo.coupon_code || "-"}</Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">Brand Classification</Typography>
                    <Typography fontWeight={700}>
                      {detailsClassInfo.brand_classification === 'big_brand_partner' ? 'Big Brand / Partner' : 'Normal Tutor'}
                    </Typography>
                  </Box>
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          {editMode ? (
            <>
              <Button onClick={() => setEditMode(false)} disabled={editSaving}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                disabled={editSaving}
              >
                {editSaving ? "Saving..." : "Save Changes"}
              </Button>
            </>
          ) : (
            <>
              {detailsClassInfo?.status === 'approved' && (
                <Button variant="contained" onClick={handleEditClick}>
                  Edit
                </Button>
              )}
              <Button onClick={closeDetailsDialog}>Close</Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Students Dialog */}
      <Dialog open={studentsDialogOpen} onClose={closeStudentsDialog} fullWidth maxWidth="md">
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Enrolled Students</Typography>
            <IconButton onClick={closeStudentsDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {studentsLoading ? (
            <Box sx={{ p: 2 }}>
              <LinearProgress />
              <Typography sx={{ mt: 2 }}>Loading enrolled students...</Typography>
            </Box>
          ) : (
            <>
              <Typography sx={{ mb: 2, fontWeight: 600 }}>
                {selectedClassInfo?.title || "Class"}
              </Typography>

              {classStudents.length === 0 ? (
                <Typography>No students have enrolled yet.</Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Phone</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Payment</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Booked At</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {classStudents.map((student) => (
                        <TableRow key={student.id} hover>
                          <TableCell>{student.student_name}</TableCell>
                          <TableCell>{student.student_email || "-"}</TableCell>
                          <TableCell>{student.student_phone || "-"}</TableCell>
                          <TableCell>
                            <Chip
                              size="small"
                              label={String(student.payment_status || '-').toUpperCase()}
                              color={student.payment_status === 'paid' ? 'success' : 'default'}
                              variant={student.payment_status === 'paid' ? 'filled' : 'outlined'}
                            />
                          </TableCell>
                          <TableCell>
                            {student.created_at ? new Date(student.created_at).toLocaleString() : "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeStudentsDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}